/**
 * Cursor 风格：Agent / Ask 模式切换（影响 WebSocket chat 的 mode 字段）
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useChatStore, type ChatInteractionMode } from '@/stores/chatStore';

const MODES: { id: ChatInteractionMode; label: string; icon: string }[] = [
  { id: 'agent', label: 'Agent', icon: '∞' },
  { id: 'ask', label: 'Ask', icon: '💬' },
];

interface ComposerModeSelectProps {
  disabled?: boolean;
  /** 为 true 时缩小触发器（用于消息条底部栏） */
  compact?: boolean;
  /** 触发器贴在容器底部时，菜单向上展开，避免被裁切 */
  menuUp?: boolean;
}

export const ComposerModeSelect: React.FC<ComposerModeSelectProps> = ({
  disabled = false,
  compact = false,
  menuUp = false,
}) => {
  const chatMode = useChatStore((s) => s.chatMode);
  const setChatMode = useChatStore((s) => s.setChatMode);
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  const current = MODES.find((m) => m.id === chatMode) ?? MODES[0];

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node;
      if (wrapRef.current && !wrapRef.current.contains(t)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc, true);
    return () => document.removeEventListener('mousedown', onDoc, true);
  }, [open]);

  const pick = useCallback(
    (id: ChatInteractionMode) => {
      setChatMode(id);
      setOpen(false);
    },
    [setChatMode]
  );

  return (
    <div
      className={`sse-composer-mode-wrap${compact ? ' sse-composer-mode-wrap--compact' : ''}${menuUp ? ' sse-composer-mode-wrap--menu-up' : ''}`}
      ref={wrapRef}
    >
      <button
        type="button"
        className="sse-composer-mode-trigger"
        disabled={disabled}
        aria-expanded={open}
        aria-haspopup="listbox"
        onClick={() => !disabled && setOpen((v) => !v)}
        title="选择 Agent（可执行工具）或 Ask（仅问答）"
      >
        <span className="sse-composer-mode-trigger-icon" aria-hidden>
          {current.icon}
        </span>
        <span className="sse-composer-mode-trigger-label">{current.label}</span>
        <span className="sse-composer-mode-trigger-chev" aria-hidden>
          ⌄
        </span>
      </button>
      {open ? (
        <ul className="sse-composer-mode-menu" role="listbox">
          {MODES.map((m) => (
            <li key={m.id} role="option" aria-selected={m.id === chatMode}>
              <button
                type="button"
                className={`sse-composer-mode-item${m.id === chatMode ? ' is-active' : ''}`}
                onClick={() => pick(m.id)}
              >
                <span className="sse-composer-mode-item-icon" aria-hidden>
                  {m.icon}
                </span>
                <span className="sse-composer-mode-item-label">{m.label}</span>
                {m.id === chatMode ? (
                  <span className="sse-composer-mode-check" aria-hidden>
                    ✓
                  </span>
                ) : null}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
};
